import { format } from 'date-fns';
import _ from 'lodash';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Actions as CONSUMPTION_ACTIONS } from '../../store/actions/consumptionActions';
import { Actions as ORGANISATION_ACTIONS } from '../../store/actions/organisationActions';
import { Actions as REPORT_ACTIONS } from '../../store/actions/reportActions';
import { Actions as FACILITY_ACTIONS } from '../../store/actions/facilityActions';
import { Actions as ADMINACTIONS } from '../../store/actions/adminActions';
import { parseDateToTz } from '../utils/GlobalFunctions';
import { Button } from '../ui/button';
import { Building, Calendar, Bookmark, Eye, FileText, ArrowRight, Loader2, FileSpreadsheet, FileDown, FileBarChart } from 'lucide-react';

const ReportOverview = ({
  reportDetails,
  organisationDetails,
  mode,
  getReportPDF,
  getReportExcel,
  excelProcessing,
  pdfProcessing,
  getConsumption,
  getReport,
  currentOrganisation,
  queryMetrics,
  industriesList,
  getOrganisation,
  getOrgHierarchy,
  getHierarchyFacilities,
  includedOrgs,
  showConsumptionAlert,
  toggleModal,
  organisationHierarchy,
  branding,
  organisations,
  saveIncludedOrgs,
  secrReport,
  getSecrReport,
  secrProcessing,
}) => {
  useEffect(() => {
    getOrgHierarchy(currentOrganisation);
    getHierarchyFacilities(currentOrganisation);
  }, []);

  useEffect(() => {
    if (!reportDetails) {
      const reportId = window.location.pathname.split('/')[3];
      getReport(reportId);
    }
    if (!organisationDetails) {
      getOrganisation();
    }
  }, [currentOrganisation, getOrganisation, getReport, reportDetails, organisationDetails]);

  useEffect(() => {
    if (reportDetails?.id) {
      const selectedOrgs = localStorage.getItem('selectedOrgs') || null;
      let childOrgs;
      if (selectedOrgs) {
        childOrgs = JSON.parse(selectedOrgs)?.map((org) => org.value);
      } else {
        childOrgs = [];
      }
      saveIncludedOrgs(childOrgs);
      getConsumption(false, false, childOrgs);
    }
  }, [reportDetails?.id, mode, getConsumption]);

  return (
    <TooltipProvider>
      <Card className='bg-card text-card-foreground rounded-b shadow-md'>
        <CardContent className='p-6'>
          <ul className='flex flex-col gap-2'>
            <li className='flex justify-between items-center py-2 border-b border-border'>
              <div className='flex items-center gap-2'>
                <Building size={16} strokeWidth={1.5} className='text-muted-foreground' />
                <span>Organisation</span>
              </div>
              <span id='organisation-name' className='font-semibold'>
                {organisations?.find((org) => org.id === currentOrganisation)?.name}
              </span>
            </li>
            <li className='flex justify-between items-center py-2 border-b border-border'>
              <div className='flex items-center gap-2'>
                <Calendar size={16} strokeWidth={1.5} className='text-muted-foreground' />
                <span>Reporting Period</span>
              </div>
              <span id='reporting-period' className='font-semibold'>
                {reportDetails && format(parseDateToTz(reportDetails.startDate, 'yyyy-MM-dd'), 'do MMM yyyy')} to{' '}
                {reportDetails && format(parseDateToTz(reportDetails.endDate, 'yyyy-MM-dd'), 'do MMM yyyy')}
              </span>
            </li>
            <li className='flex justify-between items-center py-2 border-b border-border'>
              <div className='flex items-center gap-2'>
                <Bookmark size={16} strokeWidth={1.5} className='text-muted-foreground' />
                <span>Industry Sector</span>
              </div>
              <span id='industry-sector' className='font-semibold'>
                {industriesList?.find((i) => i.id === organisationDetails?.industry)?.name}
              </span>
            </li>
            {includedOrgs?.length > 0 && (
              <li className='flex justify-between items-center py-2 border-b border-border'>
                <div className='flex items-center gap-2'>
                  <Building size={16} strokeWidth={1.5} className='text-muted-foreground' />
                  <span>Included Organisations</span>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='text-primary hover:bg-primary/10 rounded-md p-2'
                      onClick={() => toggleModal(true, `show-report-organisations`)}>
                      <Eye size={16} strokeWidth={1.5} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Show included organisations</TooltipContent>
                </Tooltip>
              </li>
            )}
            <li className='flex flex-col gap-2 py-2'>
              <div className='flex items-center justify-between gap-4 mb-2 flex-wrap'>
                <div className='flex items-center gap-2'>
                  <FileText size={16} strokeWidth={1.5} className='text-muted-foreground' />
                  <span>Reports</span>
                </div>
                <div className='flex flex-wrap gap-2'>
                  {mode === 'consumption' && (
                    <>
                      <Button asChild className='flex items-center gap-2'>
                        <Link to={`/report/${currentOrganisation}/${reportDetails?.id}/`} id='Reports-annual-carbon-footprint-report'>
                          <ArrowRight size={16} strokeWidth={1.5} className='report-icon' />
                          Annual Carbon Footprint Report
                        </Link>
                      </Button>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            className='flex items-center gap-2'
                            id='Reports-excel-report'
                            onClick={() => {
                              !excelProcessing && queryMetrics?.length > 0 && getReportExcel(reportDetails.id);
                            }}
                            disabled={queryMetrics?.length === 0}>
                            {excelProcessing ? <Loader2 size={16} strokeWidth={1.5} className='animate-spin report-icon' /> : <FileSpreadsheet size={16} strokeWidth={1.5} className='report-icon' />}
                            Excel Report
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>You need to select at least one metric to download the report</TooltipContent>
                      </Tooltip>
                      <Button
                        className='flex items-center gap-2'
                        id='Reports-pdf-report'
                        onClick={() => !pdfProcessing && getReportPDF(reportDetails.id, true, false)}>
                        {pdfProcessing ? <Loader2 size={16} strokeWidth={1.5} className='animate-spin report-icon' /> : <FileDown size={16} strokeWidth={1.5} className='report-icon' />}
                        PDF Report
                      </Button>
                    </>
                  )}
                  {mode === 'monthly' && (
                    <Button asChild className='flex items-center gap-2'>
                      <Link to={`/report/${currentOrganisation}/${reportDetails?.id}/`}>
                        <ArrowRight size={16} strokeWidth={1.5} className='report-icon' /> Annual Carbon Footprint Report
                      </Link>
                    </Button>
                  )}
                  {mode !== 'consumption' && mode !== 'monthly' && reportDetails && (
                    <>
                      <Button asChild className='flex items-center gap-2'>
                        <Link
                          to={`/consumption/${currentOrganisation}/${reportDetails?.id}/`}
                          id='Reports-annual-consumption-report'
                          onClick={() => {
                            if (organisationHierarchy === true) showConsumptionAlert(true);
                          }}>
                          <ArrowRight size={16} strokeWidth={1.5} className='report-icon' /> Annual Consumption Report
                        </Link>
                      </Button>
                      {/* {secrReport && (
                        <Button
                          className='flex items-center gap-2'
                          id='Reports-secr-report'
                          onClick={() => !secrProcessing && getSecrReport(reportDetails.id)}>
                          {secrProcessing ? <Loader2 size={16} strokeWidth={1.5} className='animate-spin report-icon' /> : <FileBarChart size={16} strokeWidth={1.5} className='report-icon' />}
                          SECR Report
                        </Button>
                      )} */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            className='flex items-center gap-2'
                            id='Reports-excel-report'
                            onClick={() => {
                              !excelProcessing && queryMetrics?.length > 0 && getReportExcel(reportDetails.id);
                            }}
                            disabled={queryMetrics?.length === 0}>
                            {excelProcessing ? <Loader2 size={16} strokeWidth={1.5} className='animate-spin report-icon' /> : <FileSpreadsheet size={16} strokeWidth={1.5} className='report-icon' />}
                            Excel Report
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>You need to select at least one metric to download the report</TooltipContent>
                      </Tooltip>
                      <Button
                        className='flex items-center gap-2'
                        id='Reports-pdf-report'
                        onClick={() => !pdfProcessing && getReportPDF(reportDetails.id, false, false)}>
                        {pdfProcessing ? <Loader2 size={16} strokeWidth={1.5} className='animate-spin report-icon' /> : <FileDown size={16} strokeWidth={1.5} className='report-icon' />}
                        PDF Report
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

const mapStateToProps = (state) => {
  const { organisation, report } = state;
  const filters = state.consumption?.filter;
  const includedOrganisations = state.report?.includedOrgs;
  const adminOrgs = state.admin?.organisations;
  const organisationList = state.organisation?.list;
  const { cyfAdmin, whiteLabelAdmin } = state.profile?.details || {};
  const adminUser = cyfAdmin || whiteLabelAdmin || false;
  const organisations = adminUser ? adminOrgs : organisationList;

  const metricName =
    organisation?.details &&
    ['intensityRatioMetric1ID', 'intensityRatioMetric2ID', 'intensityRatioMetric3ID'].map((ir, index) => {
      const orgIndustry = _.find(organisation?.industries, { id: organisation.details?.industry });
      const metricID = orgIndustry?.[ir];
      const metric = state.metric?.list?.find((metric) => metric?.id !== 230393 && metric?.id === metricID);

      const metricName = metric && `metric${index + 1}`;

      return metricName;
    });

  if (window.location.pathname.split('/')[1] === 'consumption') {
    metricName?.unshift('absolute', 'tCO2Million');
  } else if (window.location.pathname.split('/')[1] === 'report') {
    metricName?.unshift('orgEmissions', 'tCO2Million');
  }

  const updateMetricsName = [];

  filters?.forEach((metric) => {
    if (metric === 'm1') {
      updateMetricsName.push('metric1');
    } else if (metric === 'm2') {
      updateMetricsName.push('metric2');
    } else if (metric === 'm3') {
      updateMetricsName.push('metric3');
    } else if (metric === 'co2e') {
      updateMetricsName.push('orgEmissions');
    } else if (metric === 'revenue') {
      updateMetricsName.push('tCO2Million');
    } else if (metric === 'absolute') {
      updateMetricsName.push('absolute');
    }
  });

  const queryMetrics = metricName?.filter((metric) => !updateMetricsName?.includes(metric) && metric !== undefined);
  const includedOrgs = includedOrganisations?.map((org) => {
    return organisations?.find((o) => o.id === org)?.name;
  });

  const features = state?.features;
  const availableFeatures = state.organisation?.details?.features;
  const feature = features?.find((feature) => feature.featureKey === 'ORGANISATION_HIERARCHY');
  const organisationHierarchy = availableFeatures?.includes('ORGANISATION_HIERARCHY') || feature?.enabledByDefault || false;
  const secrFeature = features?.find((feature) => feature.featureKey === 'SECR_REPORT');
  const secrReport = availableFeatures?.includes('SECR_REPORT') || secrFeature?.enabledByDefault || false;

  return {
    industriesList: organisation?.industries,
    reportDetails: report?.details,
    excelProcessing: report?.download?.excel,
    pdfProcessing: report?.download?.pdf,
    secrProcessing: report?.download?.secr,
    organisationDetails: organisation?.details,
    currentOrganisation: state.currentOrganisation,
    queryMetrics,
    updateMetricsName,
    includedOrgs,
    organisationHierarchy,
    branding: state?.branding?.colors,
    organisations,
    secrReport,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getReportPDF: (id, consumption, groupByMonth) => dispatch(REPORT_ACTIONS.getReportPDF(id, consumption, groupByMonth)),
  getReportExcel: (id) => dispatch(REPORT_ACTIONS.getReportExcel(id)),
  getSecrReport: (id) => dispatch(REPORT_ACTIONS.getSecrReport(id)),
  getReport: (id) => dispatch(REPORT_ACTIONS.getReport(id)),
  getConsumption: (groupByMonth, stepFunctionSucceeded, childOrgs) =>
    dispatch(CONSUMPTION_ACTIONS.getConsumption(groupByMonth, stepFunctionSucceeded, childOrgs)),
  getOrganisation: () => dispatch(ORGANISATION_ACTIONS.getOrganisation()),
  getOrgHierarchy: (orgId) => dispatch(ORGANISATION_ACTIONS.getOrgHierarchy(orgId)),
  getHierarchyFacilities: (orgId) => dispatch(FACILITY_ACTIONS.getHierarchyFacilities(orgId)),
  showConsumptionAlert: (show) => dispatch(REPORT_ACTIONS.showConsumptionAlert(show)),
  toggleModal: (status, action, item, notAutoClose) =>
    dispatch(ADMINACTIONS.toggleModal(status, action, item, notAutoClose)),
  saveIncludedOrgs: (orgs) => dispatch(REPORT_ACTIONS.saveIncludedOrgs(orgs)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReportOverview);
